import { useState } from "react";
import Layout from "../../components/Layout.tsx";
import { StepProps } from "./AddNewConnection.tsx";
import { useNewConnectionContext } from "../../context/NewConnectionContext.tsx";
import { ApiError, postCreateConnection, putUpdateConnection } from "../../services/ApiService.ts";
import { useLocation } from "wouter";
import { useApplicationInfoContext } from "../../context/ApplicationInfoContext.tsx";
import { useUser } from "../../context/UserContext";
import toast from "react-hot-toast";
import { formatSyncSchedule } from "../../hooks/data.ts";
import { Connection, SyncModule } from "../../types.ts";
import { RiCheckLine, RiCheckboxCircleLine, RiInformationLine, RiPlayLine } from "@remixicon/react";
import { Button } from "../../components/Button.tsx";

const VAT_MODULE_LABELS: Record<string, string> = {
    INKOOP: "Exact Online - Inkoop",
    VERKOOP: "Exact Online - Verkoop",
};

const FinalOverviewStep = ({ handleButtonBackClick, stepIndicator }: StepProps) => {
    const { newConnectionData, clearStoredConnectionData } = useNewConnectionContext();
    const [, setLocation] = useLocation();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [created, setCreated] = useState(false);
    const [createdConnectionData, setCreatedConnectionData] = useState<Connection | null>(null);
    const { applicationInfo } = useApplicationInfoContext();
    const { user } = useUser();

    const displayedConnectionData = createdConnectionData ?? newConnectionData;

    const hasJournalMappings = displayedConnectionData.mappings?.some(
        (m) => m.type === "journal" && m.destination,
    ) ?? false;
    const missingInitialSyncModules = createdConnectionData?.missingInitialSyncModules ?? [];
    const requiresInitialSync = hasJournalMappings && missingInitialSyncModules.length > 0;
    const scheduleIsPaused = displayedConnectionData.synchronisationSchema?.isPaused ?? false;

    const handleButtonNextClick = async () => {
        try {
            const isUpdating = newConnectionData.id && newConnectionData.id > 0;

            if (!user?.administrationCodes?.length) {
                setErrorMessage("Geen administratie beschikbaar.");
                return;
            }

            if (isUpdating) {
                const response = await putUpdateConnection(newConnectionData.id!, newConnectionData);
                if (response.error) {
                    throw new ApiError(response.error);
                }
                toast.success('Koppeling succesvol aangepast');
                clearStoredConnectionData();
                setLocation('/koppelingen');
            } else {
                const response = await postCreateConnection(newConnectionData);
                if (response.error) {
                    throw new ApiError(response.error);
                }
                setCreatedConnectionData(response.data ?? null);
                clearStoredConnectionData();
                setCreated(true);
            }
        } catch (error) {
            console.error(`Error ${newConnectionData.id ? 'updating' : 'creating'} connection:`, error);
            const message =
                error instanceof ApiError
                    ? error.message
                    : "Er is een onverwachte fout opgetreden. Probeer het opnieuw.";
            setErrorMessage(message);
        }
    };

    const isUpdating = newConnectionData.id && newConnectionData.id > 0;

    if (created) {
        return (
            <Layout
                title="Koppeling aangemaakt"
                stepTitle=""
                showBackButton={false}
                showNextButton={false}
            >
                <div className="mx-auto max-w-md py-8 text-center">
                    <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-green-100">
                        <RiCheckboxCircleLine className="size-7 text-green-600" />
                    </div>
                    <h2 className="mt-4 text-lg font-semibold text-gray-900">
                        Koppeling succesvol aangemaakt
                    </h2>
                    <p className="mt-1 text-sm text-gray-500">
                        {displayedConnectionData.name}
                    </p>

                    {requiresInitialSync && displayedConnectionData.synchronisationSchema && (
                        <div className="mt-6 rounded-lg border border-blue-200 bg-blue-50 px-4 py-4 text-left">
                            <div className="flex items-start gap-3">
                                <RiInformationLine className="mt-0.5 size-5 shrink-0 text-blue-500" />
                                <div className="space-y-2">
                                    <p className="text-sm font-medium text-blue-900">
                                        Eerste synchronisatie vereist
                                    </p>
                                    <p className="text-sm text-blue-800">
                                        Voer eerst eenmalig een handmatige synchronisatie uit om
                                        per module een startdatum in te stellen. Daarna worden
                                        synchronisaties automatisch uitgevoerd volgens het schema.
                                    </p>
                                    <div className="flex flex-wrap gap-1.5 pt-1">
                                        {missingInitialSyncModules.map((module) => (
                                            <span
                                                key={module}
                                                className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-700"
                                            >
                                                {module}
                                            </span>
                                        ))}
                                    </div>
                                    <p className="text-xs text-blue-600">
                                        Schema: {formatSyncSchedule(displayedConnectionData.synchronisationSchema)}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                    {scheduleIsPaused && !requiresInitialSync && displayedConnectionData.synchronisationSchema && (
                        <>
                            <hr className="mt-6 border-gray-200" />
                            <div className="mt-6 space-y-2">
                                <p className="text-sm text-gray-600">
                                    De automatische synchronisatie is gepauzeerd.
                                </p>
                                <p className="text-xs text-gray-400">
                                    Planning staat klaar — {formatSyncSchedule(displayedConnectionData.synchronisationSchema)}
                                </p>
                            </div>
                        </>
                    )}

                    <div className="mt-6 flex flex-col gap-2">
                        <Button color="primary" onClick={() => setLocation('/koppelingen')}>
                            <RiPlayLine />
                            Naar koppelingen
                        </Button>
                    </div>
                </div>
            </Layout>
        );
    }

    const vatMappings = newConnectionData.mappings.filter((mapping) => mapping.type === "vatCode");
    const betalingskortingMapping = newConnectionData.mappings.find((mapping) => mapping.type === "betalingskorting");
    const vatModules = (["INKOOP", "VERKOOP"] as SyncModule[]).filter((module) =>
        vatMappings.some((mapping) => mapping.module === module)
    );
    const vatRows = Array.from(new Set(vatMappings.map((mapping) => mapping.source))).map((source) => ({
        source,
        destinationByModule: Object.fromEntries(
            vatModules.map((module) => [
                module,
                vatMappings.find((mapping) => mapping.source === source && mapping.module === module)?.destination,
            ])
        ) as Partial<Record<SyncModule, string>>,
    }));

    return (
        <Layout
            title={isUpdating ? "Koppeling aanpassen" : "Nieuwe koppeling maken"}
            stepTitle="Overzicht"
            onButtonBackClick={handleButtonBackClick}
            onButtonNextClick={handleButtonNextClick}
            showNextButton={true}
            nextButtonIcon={<RiCheckLine />}
            nextButtonLabel="Opslaan"
            stepIndicator={stepIndicator}
        >
            {errorMessage && (
                <div className="bg-red-100 text-red-600 p-2 mb-4 rounded">
                    {errorMessage}
                </div>
            )}

            <div className="space-y-4">
                <div className="flex py-2 border-b">
                    <span className="font-medium text-gray-600">Koppeling naam:</span>
                    <span className="font-bold pl-2">{newConnectionData.name}</span>
                </div>

                <div className="flex  py-2 border-b">
                    <span className="font-medium text-gray-600">Easyflex administratie:</span>
                    <span className="font-bold pl-2">{applicationInfo.availableEasyFlexAdmins.find(admin => String(admin.id) === String(newConnectionData.sourceAdministrationId))?.name}</span>
                </div>

                <div className="flex py-2 border-b">
                    <span className="font-medium text-gray-600">Exact Online administratie:</span>
                    <span className="font-bold pl-2">{applicationInfo.availableDestinations.find(admin => admin.id === newConnectionData.destinationAdministrationId)?.name}</span>
                </div>

                <div className="flex py-2 border-b">
                    <span className="font-medium text-gray-600">Synchronisatie schema:</span>
                    <span className="font-bold pl-2">
                        {formatSyncSchedule(newConnectionData.synchronisationSchema)}
                    </span>
                </div>

                <div className="flex py-2 border-b">
                    <span className="font-medium text-gray-600">Terugkijken:</span>
                    <span className="font-bold pl-2">{newConnectionData.synchronisationSchema.lookbackWeeks} weken</span>
                </div>

                <div className="flex py-2 border-b">
                    <span className="font-medium text-gray-600">Geplande uitvoering:</span>
                    <span className="font-bold pl-2">
                        {newConnectionData.synchronisationSchema.dryRun ? 'Alleen proefdraai' : 'Synchronisatie'}
                    </span>
                </div>

                <div className="flex py-2 border-b">
                    <span className="font-medium text-gray-600">Synchroniseer debiteuren:</span>
                    <span className="font-bold pl-2">{newConnectionData.enableSyncDebiteuren ? 'Ja' : 'Nee'}</span>
                </div>

                <div className="flex py-2 border-b">
                    <span className="font-medium text-gray-600">Synchroniseer grootboeken:</span>
                    <span className="font-bold pl-2">{newConnectionData.enableSyncGrootboeken ? 'Ja' : 'Nee'}</span>
                </div>

                <div className="flex py-2 border-b">
                    <span className="font-medium text-gray-600">Synchroniseer kostenplaatsen:</span>
                    <span className="font-bold pl-2">{newConnectionData.enableSyncKostenplaatsen ? 'Ja' : 'Nee'}</span>
                </div>

                <div className="py-2">
                    <p className="font-medium text-gray-600 mb-2">Gekoppelde modules:</p>
                    <table className="w-full text-left border">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="px-3 py-1">Easyflex Module</th>
                                <th className="px-3 py-1">Exact Online Module</th>
                            </tr>
                        </thead>
                        <tbody>
                            {newConnectionData.mappings
                                .filter(mapping => mapping.type === "journal")
                                .map((journalMapping, index) => (
                                    <tr key={index}>
                                        <td className="px-3 py-2">{applicationInfo.availableJournals.find(field => String(field.id) === journalMapping.source)?.name}</td>
                                        <td className="px-3 py-2">{applicationInfo.availableModules.find(field => field.Code === journalMapping.destination)?.Description}</td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>

                {vatMappings.length > 0 && (
                    <div className="py-2">
                        <p className="font-medium text-gray-600 mb-2">Gekoppelde BTW-codes:</p>
                        <table className="w-full text-left border">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="px-3 py-1">Easyflex Veld</th>
                                    {vatModules.map((module) => (
                                        <th key={module} className="px-3 py-1">{VAT_MODULE_LABELS[module]}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {vatRows.map((row) => (
                                    <tr key={row.source}>
                                        <td className="px-3 py-2">{applicationInfo.availableEasyVATCodes.find(field => String(field.id) === row.source)?.name}</td>
                                        {vatModules.map((module) => (
                                            <td key={module} className="px-3 py-2">
                                                {applicationInfo.availableDestinationVATCodes.find(field => field.Code === row.destinationByModule[module])?.Description}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {betalingskortingMapping && (
                    <div className="flex py-2 border-b">
                        <span className="font-medium text-gray-600">Betalingskorting grootboek (inkoop):</span>
                        <span className="font-bold pl-2">{betalingskortingMapping.destination}</span>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default FinalOverviewStep;
